import { useMemo } from 'react';

import { FilterDropdown } from '../../ui/FilterDropdown/FilterDropdown';
import {
  complianceStatusOptions,
  entityOptions as defaultEntityOptions,
  riskClassificationOptions as defaultRiskClassificationOptions
} from './FilterSection.constants';
import { FiltersSectionProps } from './FiltersSection.types';
import { AppliedFilters } from '../../ui/AppliedFilters/AppliedFilters';

export function FiltersSection({
  selectedEntity,
  onEntityChange,
  selectedComplianceStatus,
  onComplianceStatusChange,
  selectedRiskClassification,
  onRiskClassificationChange,
  selectedOwners,
  onOwnersChange,
  selectedOrganization,
  onOrganizationChange,
  selectedAccount = [],
  onAccountChange,
  onResetFilters,
  showRegulationScopedFilters = true,
  entityOptions = defaultEntityOptions,
  riskClassificationOptions = defaultRiskClassificationOptions,
  ownerOptions = [],
  ownersLoading = false,
  organizationOptions = [],
  organizationsLoading = false,
  accountOptions = [],
  accountsLoading = false
}: FiltersSectionProps) {
  const showAccountFilter = Boolean(onAccountChange);

  const hasActiveFilters = useMemo(() => {
    return (
      (showRegulationScopedFilters && selectedEntity.length > 0) ||
      selectedComplianceStatus.length > 0 ||
      (showRegulationScopedFilters && selectedRiskClassification.length > 0) ||
      selectedOwners.length > 0 ||
      selectedOrganization.length > 0 ||
      selectedAccount.length > 0
    );
  }, [
    showRegulationScopedFilters,
    selectedEntity,
    selectedComplianceStatus,
    selectedRiskClassification,
    selectedOwners,
    selectedOrganization,
    selectedAccount
  ]);

  return (
    <div className="flex items-center gap-2 w-full">
      <AppliedFilters />

      {showRegulationScopedFilters && (
        <div className="flex-[0.7] min-w-0">
          <FilterDropdown
            placeholder="Entity"
            options={entityOptions}
            selectedValues={selectedEntity}
            onSelectionChange={onEntityChange}
            className="w-full"
          />
        </div>
      )}

      <div className="flex-[1.2] min-w-0">
        <FilterDropdown
          placeholder="Compliance Status"
          options={complianceStatusOptions}
          selectedValues={selectedComplianceStatus}
          onSelectionChange={onComplianceStatusChange}
          className="w-full"
        />
      </div>

      {showRegulationScopedFilters && (
        <div className="flex-[1.2] min-w-0">
          <FilterDropdown
            placeholder="Risk Classification"
            options={riskClassificationOptions}
            selectedValues={selectedRiskClassification}
            onSelectionChange={onRiskClassificationChange}
            className="w-full"
          />
        </div>
      )}

      <div className="flex-[0.7] min-w-0">
        <FilterDropdown
          placeholder={ownersLoading ? 'Loading...' : 'Owner'}
          options={ownerOptions}
          selectedValues={selectedOwners}
          onSelectionChange={onOwnersChange}
          className="w-full"
        />
      </div>

      {showAccountFilter && (
        <div className="flex-[1.2] min-w-0">
          <FilterDropdown
            placeholder={accountsLoading ? 'Loading...' : 'Organization'}
            options={accountOptions}
            selectedValues={selectedAccount}
            onSelectionChange={onAccountChange!}
            className="w-full"
          />
        </div>
      )}

      <div className="flex-[1.2] min-w-0">
        <FilterDropdown
          placeholder={
            organizationsLoading ? 'Loading...' : 'Provider Organization'
          }
          options={organizationOptions}
          selectedValues={selectedOrganization}
          onSelectionChange={onOrganizationChange}
          className="w-full"
        />
      </div>

      <button
        className={`flex items-center gap-2 px-3 py-2 flex-shrink-0 transition-colors ${
          hasActiveFilters ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
        }`}
        onClick={hasActiveFilters ? onResetFilters : undefined}
        disabled={!hasActiveFilters}
      >
        <span
          className={`text-xs leading-4 font-medium ${
            hasActiveFilters ? 'text-purple-800' : 'text-gray-200'
          }`}
        >
          Reset Filters
        </span>
      </button>
    </div>
  );
}
